import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { SystemPartner } from 'api/types';
import { useUserContext } from 'context/UserContextProvider';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import { useDeleteWorkshopMutation } from 'hooks/mutations/useDeleteWorkshopMutation';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import { DangerZoneRow } from './DangerZoneRow';

type Props = {
  workshopId: number;
  systemPartnersOfWorkshop: SystemPartner[];
};

export const SoftDelete = ({ workshopId, systemPartnersOfWorkshop }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast(defaultToastOptions);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useUserContext();
  const workshopQuery = useWorkshopQuery(workshopId);
  const softDeleteWorkshopMutation = useDeleteWorkshopMutation(workshopId, 'soft');
  const isSubscriptionBlockingSoftDelete = workshopQuery.data?.subscription != null && !workshopQuery.data?.in_free_trial;
  const disabledTooltip = useMemo<string | null>(() => {
    if (!user.permissions.includes('workshops:soft-delete')) {
      return t('workshop:soft_delete.tooltip.disabled.no_permission');
    }
    if (isSubscriptionBlockingSoftDelete) {
      return t('workshop:soft_delete.tooltip.disabled.has_subscription');
    }
    return null;
  }, [isSubscriptionBlockingSoftDelete, t, user.permissions]);

  return (
    <>
      <DangerZoneRow>
        <Stack gap={0}>
          <Heading size="xxs" as="h3">
            {t('workshop:soft_delete.title')}
          </Heading>
          {isSubscriptionBlockingSoftDelete ? (
            <Text>{t('workshop:soft_delete.body.has_subscription')}</Text>
          ) : (
            <Text>{t('workshop:soft_delete.body.default')}</Text>
          )}
        </Stack>
        <Tooltip label={disabledTooltip} hasArrow>
          <Button
            leftIcon={<MdDelete size="24px" />}
            minWidth="24ch"
            colorScheme="red"
            onClick={onOpen}
            isLoading={softDeleteWorkshopMutation.isPending}
            loadingText={t('workshop:soft_delete.default.button')}
            disabled={disabledTooltip != null || softDeleteWorkshopMutation.isPending}
          >
            {t('workshop:soft_delete.default.button')}
          </Button>
        </Tooltip>
      </DangerZoneRow>

      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('workshop:soft_delete.modal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              <Trans i18nKey="workshop:soft_delete.modal.body.part_1" />
            </Text>
            <Text py="4">
              {systemPartnersOfWorkshop.length > 0 && (
                <Trans
                  i18nKey="workshop:soft_delete.modal.body.part_2_with_spf_user"
                  values={{ systemPartnerNames: systemPartnersOfWorkshop.map((partner) => partner.name!).join(', ') }}
                />
              )}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              {t('workshop:soft_delete.modal.button_secondary')}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={() => {
                softDeleteWorkshopMutation.mutate(undefined, {
                  onSuccess: (data) => {
                    onClose();
                    queryClient.invalidateQueries({ queryKey: useWorkshopQuery.getKey(workshopId) });
                    // Empty response means the workshop has been deleted immediately
                    if (data.data == null) {
                      toast({
                        title: t('workshop:soft_delete.toast.success.title'),
                        status: 'success',
                      });
                      navigate(`/app/op/workshops`);
                    } else {
                      toast({
                        title: t('workshop:soft_delete.toast.scheduled.title'),
                        description: t('workshop:soft_delete.toast.scheduled.description'),
                        status: 'info',
                      });
                    }
                  },
                  onError: (error) => {
                    toast({
                      title: error.message ?? t('errors:general'),
                      status: 'error',
                    });
                  },
                });
              }}
              isLoading={softDeleteWorkshopMutation.isPending}
              loadingText={t('workshop:soft_delete.modal.button_primary')}
            >
              {t('workshop:soft_delete.modal.button_primary')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
